import { useState } from "react";
import { IconChevronLeft, IconChevronRight } from "@/components/icons";
import {
  MONTH_LABELS_RU,
  WEEKDAY_LABELS_RU,
  buildCalendarDays,
  formatIsoDate,
  isoDatePart,
  parseIsoDate,
} from "@/lib/dates/isoDate";

type MonthState = { year: number; month: number };

function monthFromValue(start: string, end: string): MonthState {
  const date = parseIsoDate(isoDatePart(start || end)) ?? new Date();
  return { year: date.getFullYear(), month: date.getMonth() };
}

function shiftMonth(current: MonthState, delta: number): MonthState {
  const date = new Date(current.year, current.month + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}

interface DateRangeCalendarProps {
  start: string;
  end: string;
  selectingEnd: boolean;
  onSelect: (iso: string) => void;
  onClear: () => void;
}

export function DateRangeCalendar({
  start,
  end,
  selectingEnd,
  onSelect,
  onClear,
}: DateRangeCalendarProps) {
  const [visibleMonth, setVisibleMonth] = useState<MonthState>(() =>
    monthFromValue(start, end),
  );
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const todayIso = formatIsoDate(new Date());
  const startDay = isoDatePart(start);
  const endDay = isoDatePart(end);
  const previewEnd =
    selectingEnd && hoveredDay && hoveredDay >= startDay ? hoveredDay : endDay;
  const nextMonth = shiftMonth(visibleMonth, 1);
  const years = Array.from(
    { length: 131 },
    (_, index) => new Date().getFullYear() + 10 - index,
  );

  const renderMonth = ({ year, month }: MonthState) => (
    <div key={`${year}-${month}`} className="min-w-0">
      <h3 className="mb-2 text-center font-onest text-sm font-semibold text-slate-700">
        {MONTH_LABELS_RU[month]} {year}
      </h3>
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS_RU.map((day) => (
          <span
            key={day}
            className="py-1 text-center font-onest text-[10px] font-medium text-slate-400"
          >
            {day}
          </span>
        ))}
      </div>
      <div
        className="grid grid-cols-7 gap-0.5"
        onMouseLeave={() => setHoveredDay(null)}
      >
        {buildCalendarDays(year, month).map((day) => {
          if (!day.inMonth) {
            return <span key={day.iso} className="h-8" aria-hidden="true" />;
          }
          const boundary = day.iso === startDay || day.iso === endDay;
          const inRange = Boolean(
            startDay &&
            previewEnd &&
            day.iso > startDay &&
            day.iso < previewEnd,
          );
          const isPreview =
            selectingEnd &&
            hoveredDay === day.iso &&
            hoveredDay >= startDay &&
            hoveredDay !== endDay;
          return (
            <button
              key={day.iso}
              type="button"
              aria-label={day.iso}
              aria-pressed={boundary}
              onClick={() => onSelect(day.iso)}
              onMouseEnter={() => setHoveredDay(day.iso)}
              onFocus={() => setHoveredDay(day.iso)}
              className={[
                "h-8 rounded-md font-onest text-xs tabular-nums",
                boundary
                  ? "bg-brand-500 font-semibold text-white"
                  : inRange || isPreview
                    ? "bg-brand-50 text-brand-700"
                    : day.iso === todayIso
                      ? "font-semibold text-brand-700 hover:bg-brand-50"
                      : "text-slate-800 hover:bg-slate-100",
              ].join(" ")}
            >
              {day.day}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-1">
        <button
          type="button"
          title="Предыдущий месяц"
          aria-label="Предыдущий месяц"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <IconChevronLeft size={16} />
        </button>
        <div className="flex min-w-0 items-center gap-1">
          <select
            aria-label="Месяц"
            value={visibleMonth.month}
            onChange={(event) =>
              setVisibleMonth((current) => ({
                ...current,
                month: Number(event.target.value),
              }))
            }
            className="max-w-[8.5rem] rounded-md border-0 bg-transparent py-0.5 pl-1 pr-0 font-onest text-sm font-semibold text-ink focus:ring-2 focus:ring-brand-500/20"
          >
            {MONTH_LABELS_RU.map((label, month) => (
              <option key={label} value={month}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Год"
            value={visibleMonth.year}
            onChange={(event) =>
              setVisibleMonth((current) => ({
                ...current,
                year: Number(event.target.value),
              }))
            }
            className="rounded-md border-0 bg-transparent py-0.5 pl-1 pr-0 font-onest text-sm font-semibold text-ink focus:ring-2 focus:ring-brand-500/20"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          title="Следующий месяц"
          aria-label="Следующий месяц"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <IconChevronRight size={16} />
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {renderMonth(visibleMonth)}
        <div className="hidden sm:block">{renderMonth(nextMonth)}</div>
      </div>
      <div className="mt-3 flex justify-end border-t border-slate-100 pt-2">
        <button
          type="button"
          onClick={onClear}
          className="font-onest text-xs text-slate-500 hover:underline"
        >
          Сбросить
        </button>
      </div>
    </div>
  );
}
